import type { RequestOptions } from 'graphql-request';

import type {
  ChannelDTO,
  CreateOrderDTO,
  CustomerDTO,
  FulfillmentProfileDTO,
  ListingDTO,
  ListingStatus,
  ListingStrategy,
  OrderDTO,
  OrderLineDTO,
  ShippingProfileDTO,
  TagDTO,
  TagType,
  UpdateOrderDTO,
} from '@invenco/common-interface/sales';

import { DEFAULT_PAGE_SIZE } from '@/constants';
import {
  ListingsBoolExp,
  ListingsOrderByExp,
  ListingsQueryQuery,
  ListingsQueryQueryVariables,
  OrderBy,
  OrdersBoolExp,
  OrdersOrderByExp,
  OrdersQueryQuery,
  OrdersQueryQueryVariables,
} from '@/graphql/generated/seller/graphql';

import { BaseGateway, QueryRequestOptions } from '../BaseGateway';
import { GatewayArrayFilter, GatewayName, GatewayQueryProps, PaginatedResponse } from '../types';
import { listingsQuery, ordersQuery } from './gql/seller/sales';
import { graphqlOperatorMap } from './graphql';

export type OrderSortField =
  | 'id'
  | 'name'
  | 'channelName'
  | 'customerName'
  | 'status'
  | 'qtyOrdered'
  | 'totalPrice'
  | 'orderedAt'
  | 'createdAt';

export type QueryOrdersProps = GatewayQueryProps<{
  sortKey: OrderSortField;
  filters: {
    status: GatewayArrayFilter;
    channelName: GatewayArrayFilter;
    tagName: GatewayArrayFilter;
    skuName: GatewayArrayFilter;
    holdType: GatewayArrayFilter;
  };
}>;

export type ListingSortField =
  | 'id'
  | 'name'
  | 'channelName'
  | 'status'
  | 'qtyExpected'
  | 'qtyAvailable'
  | 'listingStrategy'
  | 'qtyListed'
  | 'createdAt';

export type QueryListingsProps = GatewayQueryProps<{
  sortKey: ListingSortField;
  filters: {
    status: GatewayArrayFilter;
    listingStrategy: GatewayArrayFilter;
    channelName: GatewayArrayFilter;
  };
}>;

export class SalesGateway extends BaseGateway {
  name: GatewayName = 'sales';

  async getOrder(id: string, options?: QueryRequestOptions): Promise<OrderDTO> {
    const { data } = await this.httpGet(`/v1/orders/${id}`, {
      ...options,
    });
    return data.order;
  }

  async queryOrders(
    { filters, take, search, offset = 0, sortBy }: QueryOrdersProps,
    options: { signal?: RequestOptions['signal'] },
  ): Promise<OrdersQueryQuery> {
    // Ensure limit is always a positive number
    const limit = Math.max(1, Math.abs(take ?? DEFAULT_PAGE_SIZE));
    const whereClause: OrdersBoolExp[] = [];

    if (search) {
      const searchPattern = `%${search}%`;
      whereClause.push({
        _or: [
          { name: { _ilike: searchPattern } },
          { reference: { _ilike: searchPattern } },
          { customerName: { _ilike: searchPattern } },
          { channelName: { _ilike: searchPattern } },
        ],
      });
    }

    if (filters?.status && filters.status.value.length > 0) {
      whereClause.push({
        status: { [graphqlOperatorMap[filters.status.operator]]: filters.status.value },
      });
    }
    if (filters?.channelName && filters.channelName.value.length > 0) {
      whereClause.push({
        channelName: {
          [graphqlOperatorMap[filters.channelName.operator]]: filters.channelName.value,
        },
      });
    }
    if (filters?.skuName && filters.skuName.value.length > 0) {
      whereClause.push({
        orderLines: {
          skuName: { [graphqlOperatorMap[filters.skuName.operator]]: filters.skuName.value },
        },
      });
    }
    if (filters?.holdType && filters.holdType.value.length > 0) {
      whereClause.push({
        orderHolds: {
          type: { [graphqlOperatorMap[filters.holdType.operator]]: filters.holdType.value },
        },
      });
    }
    if (filters?.tagName && filters.tagName.value.length > 0) {
      whereClause.push({
        tagEdges: {
          tag: {
            name: { [graphqlOperatorMap[filters.tagName.operator]]: filters.tagName.value },
          },
        },
      });
    }

    const orderBy =
      sortBy?.map<OrdersOrderByExp>((s) => ({
        [s.key]: s.direction === 'desc' ? OrderBy.Desc : OrderBy.Asc,
      })) ?? [];

    const variables: OrdersQueryQueryVariables = {
      limit,
      offset,
      where: whereClause.length > 0 ? { _and: whereClause } : {},
      orderBy,
    };

    return this.graphqlQuery(ordersQuery, variables, options.signal);
  }

  async queryListings(
    { filters, take, search, offset = 0, sortBy }: QueryListingsProps,
    options: { signal?: RequestOptions['signal'] },
  ): Promise<ListingsQueryQuery> {
    // Ensure limit is always a positive number
    const limit = Math.max(1, Math.abs(take ?? DEFAULT_PAGE_SIZE));
    const whereClause: ListingsBoolExp[] = [];

    if (search) {
      const searchPattern = `%${search}%`;
      whereClause.push({
        _or: [
          { name: { _ilike: searchPattern } },
          { description: { _ilike: searchPattern } },
          { channelName: { _ilike: searchPattern } },
          { skuName: { _ilike: searchPattern } },
          { externalSkuName: { _ilike: searchPattern } },
        ],
      });
    }

    if (filters?.status && filters.status.value.length > 0) {
      whereClause.push({
        status: { [graphqlOperatorMap[filters.status.operator]]: filters.status.value },
      });
    }
    if (filters?.listingStrategy && filters.listingStrategy.value.length > 0) {
      whereClause.push({
        listingStrategy: {
          [graphqlOperatorMap[filters.listingStrategy.operator]]: filters.listingStrategy.value,
        },
      });
    }
    if (filters?.channelName && filters.channelName.value.length > 0) {
      whereClause.push({
        channelName: {
          [graphqlOperatorMap[filters.channelName.operator]]: filters.channelName.value,
        },
      });
    }

    const orderBy =
      sortBy?.map<ListingsOrderByExp>((s) => ({
        [s.key]: s.direction === 'desc' ? OrderBy.Desc : OrderBy.Asc,
      })) ?? [];

    const variables: ListingsQueryQueryVariables = {
      limit,
      offset,
      where: whereClause.length > 0 ? { _and: whereClause } : {},
      orderBy,
    };

    return this.graphqlQuery(listingsQuery, variables, options.signal);
  }

  async getOrders(
    query?: {
      take?: number;
      cursor?: string;
      status?: string;
      search?: string;
      holds?: string[];
      skuNames?: string[];
      tagNames?: string[];
      channelNames?: string[];
    },
    options?: QueryRequestOptions,
  ): Promise<PaginatedResponse<OrderDTO>> {
    const params = {
      take: query?.take,
      cursor: query?.cursor,
      status: query?.status,
      q: query?.search,
      holds: query?.holds?.join(','),
      skuNames: query?.skuNames?.join(','),
      tagNames: query?.tagNames?.join(','),
      channelNames: query?.channelNames?.join(','),
    };
    const { data } = await this.httpGet('/v1/orders', { params, ...options });
    return { ...data, items: data.orders };
  }

  async createOrder(order: CreateOrderDTO): Promise<OrderDTO> {
    const { data } = await this.httpPost<{ order: OrderDTO }>('/v1/orders', {
      data: { order },
    });
    return data.order;
  }

  async updateOrder(id: string, order: UpdateOrderDTO): Promise<OrderDTO> {
    const { data } = await this.httpPatch<{ order: OrderDTO }>(`/v1/orders/${id}`, {
      data: { order },
    });
    return data.order;
  }

  async releaseOrder(id: string) {
    return this.requestOrderAction(id, 'release');
  }

  async cancelOrder(id: string) {
    return this.requestOrderAction(id, 'cancel');
  }

  async closeOrder(id: string) {
    return this.requestOrderAction(id, 'close');
  }

  async applyManualHold(id: string) {
    return this.requestOrderAction(id, 'apply_manual_hold');
  }

  async releaseManualHold(id: string) {
    return this.requestOrderAction(id, 'release_manual_hold');
  }

  async revertToDraft(id: string) {
    return this.requestOrderAction(id, 'revert_to_draft');
  }

  private async requestOrderAction(id: string, actionUri: string): Promise<OrderDTO> {
    const { data } = await this.httpPost<{ order: OrderDTO }>(`/v1/orders/${id}/${actionUri}`, {});
    return data.order;
  }

  async updateOrderLine(orderId: string, orderLine: Partial<OrderLineDTO>): Promise<OrderLineDTO> {
    const { id, ...updateProps } = orderLine;
    const { data } = await this.httpPatch<{ orderLine: OrderLineDTO }>(
      `/v1/orders/${orderId}/order_lines/${id}`,
      {
        data: { orderLine: updateProps },
      },
    );
    return data.orderLine;
  }

  async addOrderLine(
    orderId: string,
    orderLine: Partial<OrderLineDTO>, // CreateOrderLineDTO isn't correct
  ): Promise<OrderLineDTO> {
    const { data } = await this.httpPost<{ orderLine: OrderLineDTO }>(
      `/v1/orders/${orderId}/order_lines`,
      {
        data: { orderLine },
      },
    );
    return data.orderLine;
  }

  async getChannel(id: string, options?: QueryRequestOptions): Promise<ChannelDTO> {
    const { data } = await this.httpGet(`/v1/channels/${id}`, {
      ...options,
    });
    return data.channel;
  }

  async getChannels(
    query?: {
      take?: number;
      cursor?: string;
      search?: string;
    },
    options?: QueryRequestOptions,
  ): Promise<PaginatedResponse<ChannelDTO>> {
    const params = {
      take: query?.take,
      cursor: query?.cursor,
      q: query?.search,
    };
    const { data } = await this.httpGet('/v1/channels', { params, ...options });
    return { ...data, items: data.channels };
  }

  async getCustomers(
    query?: {
      take?: number;
      cursor?: string;
      search?: string;
    },
    options?: QueryRequestOptions,
  ): Promise<PaginatedResponse<CustomerDTO>> {
    const params = {
      take: query?.take,
      cursor: query?.cursor,
      q: query?.search,
    };
    const { data } = await this.httpGet('/v1/customers', { params, ...options });
    return { ...data, items: data.customers };
  }

  async getShippingProfile(id: string, options?: QueryRequestOptions): Promise<ShippingProfileDTO> {
    const { data } = await this.httpGet(`/v1/shipping_profiles/${id}`, {
      ...options,
    });
    return data.shippingProfile;
  }

  async getShippingProfiles(
    input?: {
      search?: string;
    },
    options?: QueryRequestOptions,
  ): Promise<{ items: ShippingProfileDTO[] }> {
    const { data } = await this.httpGet('/v1/shipping_profiles', {
      params: {
        q: input?.search,
      },
      ...options,
    });
    return { ...data, items: data.shippingProfiles };
  }

  async createShippingProfile(
    shippingProfile: Partial<ShippingProfileDTO>,
  ): Promise<ShippingProfileDTO> {
    const { data } = await this.httpPost<{ shippingProfile: ShippingProfileDTO }>(
      '/v1/shipping_profiles',
      {
        data: { shippingProfile },
      },
    );
    return data.shippingProfile;
  }

  async updateShippingProfile(
    shippingProfile: Partial<ShippingProfileDTO>,
  ): Promise<ShippingProfileDTO> {
    const { id, ...updateData } = shippingProfile;
    const { data } = await this.httpPatch<{ shippingProfile: ShippingProfileDTO }>(
      `/v1/shipping_profiles/${id}`,
      {
        data: { shippingProfile: updateData },
      },
    );
    return data.shippingProfile;
  }

  async deleteShippingProfile(id: string) {
    await this.httpDelete(`/v1/shipping_profiles/${id}`);
  }

  async getListings(
    query?: {
      take?: number;
      cursor?: string;
      search?: string;
      statuses?: ListingStatus[];
      channelIds?: string[];
      channelNames?: string[];
      skuIds?: string[];
      strategies?: ListingStrategy[];
    },
    options?: QueryRequestOptions,
  ): Promise<PaginatedResponse<ListingDTO>> {
    const params = {
      take: query?.take,
      cursor: query?.cursor,
      q: query?.search,
      statuses: query?.statuses?.join(','),
      skuIds: query?.skuIds?.join(','),
      channelIds: query?.channelIds?.join(','),
      channelNames: query?.channelNames?.join(','),
      strategies: query?.strategies?.join(','),
    };
    const { data } = await this.httpGet('/v1/listings', { params, ...options });
    return { ...data, items: data.listings };
  }

  async createListing(listing: Partial<ListingDTO>): Promise<ListingDTO> {
    const { data } = await this.httpPost<{ listing: ListingDTO }>('/v1/listings', {
      data: { listing },
    });
    return data.listing;
  }

  async updateListing(listing: Partial<ListingDTO>): Promise<ListingDTO> {
    const { id, ...updateData } = listing;
    const { data } = await this.httpPatch<{ listing: ListingDTO }>(`/v1/listings/${id}`, {
      data: { listing: updateData },
    });
    return data.listing;
  }

  async refreshListing(listingId: string): Promise<ListingDTO> {
    const { data } = await this.httpPost<{ listing: ListingDTO }>(
      `/v1/listings/${listingId}/refresh`,
    );
    return data.listing;
  }

  async getTags(
    query?: {
      take?: number;
      cursor?: string;
      search?: string;
      type?: TagType;
    },
    options?: QueryRequestOptions,
  ): Promise<PaginatedResponse<TagDTO>> {
    const params = {
      take: query?.take,
      cursor: query?.cursor,
      q: query?.search,
      type: query?.type,
    };
    const { data } = await this.httpGet('/v1/tags', { params, ...options });
    return { ...data, items: data.tags };
  }

  async getFulfillmentProfiles(
    query?: {
      take?: number;
      cursor?: string;
      search?: string;
    },
    options?: QueryRequestOptions,
  ): Promise<PaginatedResponse<FulfillmentProfileDTO>> {
    const params = {
      take: query?.take,
      cursor: query?.cursor,
      q: query?.search,
    };
    const { data } = await this.httpGet('/v1/fulfillment_profiles', { params, ...options });
    return { ...data, items: data.fulfillmentProfiles };
  }
}
